import React, { useState } from 'react';
import {
  CANAL_TIENDA_ACTORS,
  KAABA_CANAL_CHECKLIST,
  stateLabel,
  stateColor
} from '../constants/ecosystemProjects';
import './EcosystemPanels.css';

const PRIORITY_LABEL = { alta: 'Alta', media: 'Media', baja: 'Baja' };
const CANAL_TASKS_KEY = 'javiland_canal_tienda_tasks';
const KAABA_CHECKLIST_KEY = 'javiland_kaaba_checklist';

const loadJson = (key, fallback = {}) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const CanalTiendaPanel = ({ onNavigate }) => {
  const [doneTasks, setDoneTasks] = useState(() => loadJson(CANAL_TASKS_KEY));
  const [kaabaSteps, setKaabaSteps] = useState(() => loadJson(KAABA_CHECKLIST_KEY));
  const [copyOk, setCopyOk] = useState(false);

  const kaaba = CANAL_TIENDA_ACTORS.find((a) => a.id === 'kaaba');
  const followUps = CANAL_TIENDA_ACTORS.filter((a) => a.followUp);

  const persist = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  };

  const toggleTask = (id) => {
    setDoneTasks((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persist(CANAL_TASKS_KEY, next);
      return next;
    });
  };

  const toggleKaabaStep = (idx) => {
    setKaabaSteps((prev) => {
      const next = { ...prev, [idx]: !prev[idx] };
      persist(KAABA_CHECKLIST_KEY, next);
      return next;
    });
  };

  const copyWhatsApp = async () => {
    const text = kaaba?.whatsappTemplate || '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2500);
    } catch {
      window.prompt('Copia el mensaje:', text);
    }
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent(kaaba?.whatsappTemplate || '');
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="eco-panel">
      <header className="eco-panel-header">
        <h2>Canal tienda y proveedores</h2>
        <p>SKUs, dropshipping y alianzas que alimentan javiland.com — Annita solo aquí, no en Cotizaciones.</p>
      </header>

      {kaaba ? (
        <section className="eco-kaaba-box" aria-label="Plan Kaaba Café">
          <div className="eco-kaaba-box-header">
            <h3>
              <i className="fas fa-mug-hot" aria-hidden /> Kaaba Café — canal tienda (prioridad)
            </h3>
            <span
              className="eco-state-pill eco-state-pill--sm"
              style={{ backgroundColor: stateColor(kaaba.canalState), color: '#fff' }}
            >
              {stateLabel(kaaba.canalState)}
            </span>
          </div>
          <p className="eco-kaaba-lead">
            GP web F1 en espera. Foco: café 100% colombiano (Huila) en tienda Javiland · categoría{' '}
            <code>consumo-consciente</code>.
          </p>
          <ol className="eco-kaaba-checklist">
            {KAABA_CANAL_CHECKLIST.map((step, idx) => (
              <li key={step}>
                <label>
                  <input
                    type="checkbox"
                    checked={!!kaabaSteps[idx]}
                    onChange={() => toggleKaabaStep(idx)}
                  />
                  <span>{step}</span>
                </label>
              </li>
            ))}
          </ol>
          <div className="eco-kaaba-actions">
            <button type="button" className="eco-btn eco-btn--wa" onClick={openWhatsApp}>
              <i className="fab fa-whatsapp" aria-hidden /> Abrir WhatsApp
            </button>
            <button type="button" className="eco-btn eco-btn--ghost" onClick={copyWhatsApp}>
              <i className="fas fa-copy" aria-hidden /> {copyOk ? 'Copiado' : 'Copiar mensaje'}
            </button>
            {typeof onNavigate === 'function' ? (
              <button
                type="button"
                className="eco-btn eco-btn--ghost"
                onClick={() => onNavigate('products', '')}
              >
                <i className="fas fa-box" aria-hidden /> Ir a Productos
              </button>
            ) : null}
          </div>
          {kaaba.doc ? (
            <p className="eco-card-notes">
              Referencia: {kaaba.doc} · PROPUESTA_COMERCIAL_KAABA_CAFE_FEB2026.pdf
            </p>
          ) : null}
        </section>
      ) : null}

      {followUps.length > 0 ? (
        <section className="eco-followup-box" aria-label="Seguimiento comercial">
          <h3>
            <i className="fas fa-bell" aria-hidden /> Seguimiento pendiente
          </h3>
          <ul className="eco-followup-list">
            {followUps.map((a) => (
              <li key={`fu-${a.id}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={!!doneTasks[a.id]}
                    onChange={() => toggleTask(a.id)}
                  />
                  <span>
                    <strong>{a.name}:</strong> {a.followUpHint || a.blocker}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className="eco-followup-note">
            También puedes dar seguimiento por WhatsApp; esto es recordatorio en el panel.
          </p>
        </section>
      ) : null}

      <div className="eco-table-wrap">
        <table className="eco-table">
          <thead>
            <tr>
              <th>Actor</th>
              <th>Estado canal</th>
              <th>Prioridad</th>
              <th>Bloqueador</th>
              <th>Próximo paso</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {CANAL_TIENDA_ACTORS.map((row) => (
              <tr key={row.id} className={row.id === 'kaaba' ? 'eco-table-row--highlight' : ''}>
                <td>
                  <strong>{row.name}</strong>
                  {row.doc ? (
                    <div className="eco-table-doc" title={row.doc}>
                      {row.doc}
                    </div>
                  ) : null}
                </td>
                <td>
                  <span
                    className="eco-state-pill eco-state-pill--sm"
                    style={{ backgroundColor: stateColor(row.canalState), color: '#fff' }}
                  >
                    {stateLabel(row.canalState)}
                  </span>
                </td>
                <td>{PRIORITY_LABEL[row.priority] || row.priority}</td>
                <td>{row.blocker || '—'}</td>
                <td>{row.nextStep || '—'}</td>
                <td>
                  {row.id === 'himalaya' && typeof onNavigate === 'function' ? (
                    <button
                      type="button"
                      className="eco-link-btn"
                      onClick={() => onNavigate('orders', 'dropshipping')}
                    >
                      Cola dropship
                    </button>
                  ) : row.id === 'kaaba' || row.id === 'jdn' || row.id === 'nutrisetas' ? (
                    <button
                      type="button"
                      className="eco-link-btn"
                      onClick={() => onNavigate('products', '')}
                    >
                      Productos
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CanalTiendaPanel;
